import { useState, useEffect, useMemo } from 'react';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { mergePartnerData, isRequiredOnSurface } from '../utils/partnerData';
import { getSelectedPartner, invalidateSelectedPartner, resolvePartnerFromList, storeSelectedPartner } from '../utils/partnerSelection';
import { useLanguage } from '../contexts/LanguageContext';

const PARTNER_CACHE_TTL_MS = 10_000;
const partnerResponseCache = new Map();

function fetchPartnerStats(url) {
  const cached = partnerResponseCache.get(url);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const promise = api.get(url).catch((error) => {
    if (partnerResponseCache.get(url)?.promise === promise) {
      partnerResponseCache.delete(url);
    }
    throw error;
  });

  partnerResponseCache.set(url, {
    promise,
    expiresAt: Date.now() + PARTNER_CACHE_TTL_MS,
  });
  return promise;
}

/**
 * Fetches and normalizes partner data from TicketTrackingPartners.
 *
 * TicketTrackingPartners is the public source for partner metadata and stats.
 * TicketTrackingFields is optional authenticated fallback data used to preserve
 * checkout partner options if an older API response is missing partners.
 *
 * @param {number|null} limit - Max number of partners to return. null = all.
 * @param {boolean} enabled - When false, skip fetching and return empty immediately.
 * @param {string|null} organizationId - Active organization id. Falls back to runtime config.
 * @param {boolean} includeHidden - When true, include partners hidden from the
 *   checkout dropdown (isVisible=false) so public display surfaces still show
 *   their stats. Disabled partners are always excluded.
 * @param {string|null} period - Filter stats by period (currentDraw, monthly, yearly, alltime).
 * @param {string|null} surface - When provided, only partners whose DisplayConfig includes this
 *   surface are returned (e.g. 'Hero', 'Leaderboard', 'RecentSupporters').
 *   Partners with no DisplayConfig fall back to the default (PurchasePage + Checkout).
 * @returns {{ partners: Array, fieldLabel: string, displayConfig: object|null, loading: boolean, error: string|null }}
 */
export default function usePartnerData(limit = null, enabled = true, organizationId = null, includeHidden = false, period = null, surface = null) {
  const [partners, setPartners] = useState([]);
  const [fieldLabel, setFieldLabel] = useState('');
  const [displayConfig, setDisplayConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { language } = useLanguage();

  useEffect(() => {
    const { tlfId } = getConfig();
    const resolvedOrganizationId = organizationId || tlfId;

    if (!enabled || !resolvedOrganizationId) {
      setPartners([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPartners = async () => {
      setLoading(true);
      setError(null);

      // TicketTrackingFields requires auth — skip when unauthenticated to
      // avoid 401 console noise on public pages (Leaderboard, partner pages).
      const hasAuthToken = !!(
        window.sessionStorage?.getItem('access_token') ||
        window.localStorage?.getItem('access_token')
      );

      const langParam = language ? `&language=${encodeURIComponent(language)}` : '';
      const periodParam = period ? `&period=${encodeURIComponent(period)}` : '';
      const partnersUrl = `${apiRoutes.TicketTrackingPartners}?orgId=${encodeURIComponent(resolvedOrganizationId)}${langParam}${periodParam}`;

      const [fieldsResult, partnersResult] = await Promise.allSettled([
        api.get(`${apiRoutes.TicketTrackingFields}?OrgId=${encodeURIComponent(resolvedOrganizationId)}${langParam}`).catch(() => null),
        fetchPartnerStats(partnersUrl)
      ]);

      if (cancelled) return;

      // Extract partners from tracking fields (reliable source)
      let fieldPartners = [];
      let resolvedFieldLabel = '';
      if (fieldsResult.status === 'fulfilled' && fieldsResult.value?.data) {
        const payload = fieldsResult.value?.data?.payload;
        const fields =
          payload?.ticketTrackingFields ||
          payload?.TicketTrackingFields ||
          (Array.isArray(payload) ? payload : []);
        const seen = new Set();

        for (const field of fields) {
          const options = field.ticketTrackingFieldOptions || field.TicketTrackingFieldOptions || [];
          const hasPartnerOptions = options.some(opt => (opt.ticketTrackingPartnerId != null || opt.TicketTrackingPartnerId != null));
          if (hasPartnerOptions && !resolvedFieldLabel) {
            resolvedFieldLabel = field.fieldName || field.FieldName || field.name || field.Name || '';
          }
          for (const opt of options) {
            const optPartnerId = opt.ticketTrackingPartnerId ?? opt.TicketTrackingPartnerId;
            if (
              optPartnerId != null &&
              !seen.has(optPartnerId)
            ) {
              seen.add(optPartnerId);
              fieldPartners.push({
                id: optPartnerId,
                name: opt.fieldValue || opt.FieldValue,
                sortOrder: opt.sortOrder ?? opt.SortOrder ?? 0,
              });
            }
          }
        }
      }

      let statsPartners = [];
      let fieldDisplayConfig = null;
      if (partnersResult.status === 'fulfilled') {
        // Backend wraps the array in payload.ticketTrackingPartners; older
        // builds returned payload as the array directly. Support both.
        const payload = partnersResult.value?.data?.payload;
        statsPartners = Array.isArray(payload)
          ? payload
          : payload?.ticketTrackingPartners || payload?.TicketTrackingPartners || [];

        // Extract fieldName from the partners response if not already resolved
        if (!resolvedFieldLabel) {
          resolvedFieldLabel =
            payload?.fieldName ||
            payload?.FieldName ||
            payload?.fieldLabel ||
            payload?.FieldLabel ||
            payload?.name ||
            payload?.Name ||
            '';
        }

        // Extract field-level displayConfig from the partners response.
        // This is the field-level default; per-partner values still override.
        if (payload?.displayConfig || payload?.DisplayConfig) {
          const rawConfig = payload?.displayConfig || payload?.DisplayConfig;
          try {
            fieldDisplayConfig = typeof rawConfig === 'string'
              ? JSON.parse(rawConfig)
              : rawConfig;
          } catch {
            fieldDisplayConfig = null;
          }
        }
      }

      const merged = mergePartnerData(fieldPartners, statsPartners, limit, {
        includeHidden,
        surface,
        displayConfig: fieldDisplayConfig,
        partnerSortOrder: fieldDisplayConfig?.partnerSortOrder || 'alphabetical'
      });

      // Both failed — surface an error but still return empty array
      if (
        fieldsResult.status === 'rejected' &&
        partnersResult.status === 'rejected'
      ) {
        setError('Failed to load partner data');
      }

      setFieldLabel(resolvedFieldLabel);
      setDisplayConfig(fieldDisplayConfig);
      setPartners(merged);
      const allKnownPartners = mergePartnerData(fieldPartners, statsPartners, null, {
        includeHidden: true,
        displayConfig: fieldDisplayConfig,
      });
      if (allKnownPartners.length > 0) {
        const currentSelected = getSelectedPartner();
        if (currentSelected) {
          const resolved = resolvePartnerFromList(merged, currentSelected)
            || resolvePartnerFromList(allKnownPartners, currentSelected);
          if (!resolved) {
            invalidateSelectedPartner(currentSelected);
          } else if (
            resolved.id &&
            (!currentSelected.id || !currentSelected.name || String(resolved.id) !== String(currentSelected.id))
          ) {
            storeSelectedPartner(resolved);
          }
        }
      }
      setLoading(false);
    };

    fetchPartners();

    return () => {
      cancelled = true;
    };
  }, [limit, enabled, organizationId, language, includeHidden, period, surface]);

  const required = useMemo(
    () => isRequiredOnSurface(partners, surface, displayConfig),
    [partners, surface, displayConfig]
  );

  return { partners, fieldLabel, displayConfig, loading, error, required };
}
